import React, {useEffect, useState} from 'react'
import {
    useMultiStyleConfig,
    VStack,
    Text,
    Flex,
    Button,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {FormattedMessage, useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {ChevronRight} from '../../../components/icons'
import PropTypes from 'prop-types'
import CategoriesList from '../partials/categories-list'
import routingObject from '../../../utils/static-routes'
import {decodeHtmlEntities} from '../../../utils/utils'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import LoadingSpinner from '../../../components/loading-spinner'
import {Helmet} from 'react-helmet'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useGoogleTagManager} from '../../../custom-api/hooks/use-google-tag-manager'
import {basic_gtm_event} from '../../../custom-api/layers/default'

const SearchResults = ({articles, setGoBackText, setPageTitle, setGoBackToPath, isLoading}) => {
    const styles = useMultiStyleConfig('Faq', {})
    const {articlesListStyles} = styles || {}
    const [visibleCount, setVisibleCount] = useState(5)
    const {locale} = useMultiSite()
    const {customParams} = getConfig()
    const intl = useIntl()

    const itemListElement = articles.map((article, index) => {
        const cleanTitle = decodeHtmlEntities(article._source.article_title)
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[?]/g, '')
        const articleUrl = `${routingObject.FAQ_PATH}/${cleanTitle}/a/${article._source.article_id}`
        return {
            '@type': 'ListItem',
            position: index + 1,
            url: `https://${customParams.currentHost}/${locale?.alias}${articleUrl}`,
        }
    })

    const faqPairs = articles.map((article) => ({
        question: decodeHtmlEntities(article._source.article_title),
        answer: article._source.article_content
            ? article._source.article_content
                  .replace(/<[^>]+>/g, '')
                  .replace(/\s+/g, ' ')
                  .trim()
            : '',
    }))

    const faqSchemaMarkup = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqPairs
            .filter((faq) => faq.question && faq.answer)
            .map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: faq.answer,
                },
            })),
    }

    const itemListSchemaMarkup = {
        '@context': 'http://schema.org',
        '@type': 'ItemList',
        itemListElement,
    }

    useEffect(() => {
        setGoBackText('default')
        setPageTitle('default')
        setGoBackToPath(routingObject.FAQ_PATH)
    }, [])

    const handleShowMore = () => {
        showMoreGtmEvent()
        setVisibleCount((prevCount) => prevCount + 10)
    }

    const buildUrl = (articleName, articleId) => {
        let cleanName = decodeHtmlEntities(articleName)
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[?]/g, '')
        let url = `${routingObject.FAQ_PATH}/${cleanName}/a/${articleId}`
        return url
    }

    const {addLayer} = useGoogleTagManager()
    const handleOpenFaqClick = (title) => {
        addLayer(
            basic_gtm_event.bind(this, {
                event: 'generic',
                eventCategory: 'engagement',
                eventAction: 'paperless-faq_single',
                eventLabel: title || '',
            }),
        )
    }
    const showMoreGtmEvent = () => {
        let cta = intl.formatMessage({
            id: 'faq_page.article_list.cta_button.text',
            defaultMessage: 'Show More',
        })
        addLayer(
            basic_gtm_event.bind(this, {
                event: 'generic',
                eventCategory: 'engagement',
                eventAction: 'paperless-faq_all',
                eventLabel: cta || '',
            }),
        )
    }

    let content
    if (isLoading) {
        content = <LoadingSpinner />
    } else if (articles.length > 0) {
        content = (
            <>
                {articles.slice(0, visibleCount).map((article) => (
                    <Flex
                        {...articlesListStyles?.articleWrapper}
                        key={article?._source?.article_id}
                    >
                        <Flex {...articlesListStyles?.articleInnerContainer}>
                            <ChevronRight {...articlesListStyles?.chevronIcon} />
                            <Link
                                to={`/${locale?.alias}${buildUrl(
                                    article?._source?.category_name,
                                    article?._source?.article_id,
                                )}`}
                                onClick={() => handleOpenFaqClick(article?._source?.article_title)}
                            >
                                <Text {...articlesListStyles?.articleLink}>
                                    {article?._source?.article_title}
                                </Text>
                            </Link>
                        </Flex>
                    </Flex>
                ))}
            </>
        )
    } else content = <CategoriesList />

    return (
        <VStack w="100%" h="100%" alignItems="flex-start">
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(itemListSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(faqSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            {content}
            <VStack {...articlesListStyles?.toogleCtaContainer}>
                {visibleCount < articles.length ? (
                    <Button {...articlesListStyles?.toogleCtaButton} onClick={handleShowMore}>
                        <Text {...articlesListStyles?.toogleText}>
                            <FormattedMessage
                                id="faq_page.article_list.cta_button.text"
                                defaultMessage="Show More"
                            />
                        </Text>
                    </Button>
                ) : (
                    <></>
                )}
            </VStack>
        </VStack>
    )
}

SearchResults.propTypes = {
    articles: PropTypes.array,
    setGoBackText: PropTypes.func,
    setPageTitle: PropTypes.func,
    setGoBackToPath: PropTypes.func,
    isLoading: PropTypes.bool,
}

export default SearchResults
